/**
 * @component SVCSPD001_21TB
 * @title 계좌 > 스피드조회 등록 > 증명서 발급확인 탭
 * @description 계좌 > 스피드조회 등록 > 증명서 발급확인 탭
 * @asis H95101A
 * @properties
 * @linked-menu
 * @author 곽동현
 * @history
 *   [2026-04-27][곽동현] 초기 작성
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageType, useThrottle } from 'scbk-services';
import {
    useSvcGstDtlGetDepositBalanceCertificate,
    useSvcGstDtlGetFinancialTransactionConfirmation,
    useSvcGstDtlGetFindIssuedCertificates,
} from 'scbk-servicing/hooks/api';
import { useSvcGstDtlGetLiabilityBalanceCertificate } from 'scbk-servicing/hooks/api/svcGstFindDetailHooks';
import {
    SvcGstDtlGetDepositBalanceCertificateResponse,
    SvcGstDtlGetFinancialTransactionConfirmationResponse,
    SvcGstDtlGetFindIssuedCertificatesResponse,
    SvcGstDtlGetLiabilityBalanceCertificateResponse,
} from 'scbk-servicing/types/api';
import { certificatesUtils } from 'scbk-servicing/utils';
import {
    dateUtils,
    Dialog,
    EdocIbReportMode,
    EdocTranType,
    EdocViewComponent,
    EdocViewParameter,
    formatUtils,
    useCode,
    useEdocView,
    withPageComponent,
} from 'scbk-shared-module';
import {
    AccordionUnder,
    BulletList,
    BulletListItem,
    Button,
    DatePickerSingle,
    Empty,
    FieldRound,
    FieldWrapper,
    GradientInfoBox,
    Images,
    InfoListGroup,
    InfoListItem,
    LineIcon,
    NativeSelect,
    NativeSelectOption,
    PhoneField,
    Radio,
    TitleList,
} from 'scbk-ui-web';

export default withPageComponent(
    'SVCSPD001_21TB',
    {
        type: PageType.MAIN_SCREEN,
        layoutOptions: {
            description: '계좌 > 스피드조회 등록 > 증명서 발급확인 탭',
        },
    },
    function () {
        const { t } = useTranslation();
        const { selectCodes } = useCode(['CURRENCY']);
        const [phone1, setPhone1] = useState('010');
        const [phone2, setPhone2] = useState('');
        const [phone3, setPhone3] = useState('');

        const edoc = useEdocView(); //전자문서

        const getToday = () => {
            return String(formatUtils.toDate(dateUtils.getToday(), '.'));
        };

        const [singleDate, setSingleDate] = useState('');
        const [birthNumValue, setBirthNumValue] = useState<string>('');
        const [btnValue, setBtnValue] = useState<{ agreeCheck?: string }>({ agreeCheck: '' });

        const btnRef = useRef<HTMLDivElement>(null); // 동의여부
        const myNameRef = useRef<HTMLInputElement>(null); // 소지인명
        const phone1Ref = useRef<HTMLSelectElement>(null); // 휴대폰번호1
        const phone2Ref = useRef<HTMLInputElement>(null); // 휴대폰번호2
        const phone3Ref = useRef<HTMLInputElement>(null); // 휴대폰번호3
        const issueNumRef = useRef<HTMLInputElement>(null); // 발급번호
        const issueDateRef = useRef<HTMLInputElement>(null); // 발급기준일
        const issueNameRef = useRef<HTMLInputElement>(null); // 예금주
        const birthNumRef = useRef<HTMLInputElement>(null); // 생년월일
        const acctNumRef = useRef<(HTMLInputElement | null)[]>([]); // 계좌번호
        const issueAmtRef = useRef<(HTMLInputElement | null)[]>([]); // 발급금액

        const currYear = Number(dateUtils.getYear());
        const limitDate = formatUtils.toDate(dateUtils.getToday(), '/');

        useEffect(() => {
            setSingleDate(getToday());
        }, []);

        type AccountGroup = {
            id: number;
            currency: string;
            accountNumber: string;
            amount: string;
        };

        const [accountGroups, setAccountGroups] = useState<AccountGroup[]>([
            { id: Date.now(), currency: '000', accountNumber: '', amount: '' },
        ]);

        const MaxAccountCount = 7;
        const handleAddGroup = () => {
            if (accountGroups.length >= MaxAccountCount) return;
            setAccountGroups((prev) => [
                ...prev,
                { id: Date.now(), currency: '000', accountNumber: '', amount: '' },
            ]);
        };

        type RadioValue = 'accountBalance' | 'transaction' | 'outstandingBalance';
        const [radioValue, setRadioValue] = useState<RadioValue>('accountBalance');

        useEffect(() => {
            setBtnValue({ agreeCheck: '' });
            resetInput(myNameRef);
            setPhone1('010');
            setPhone2('');
            setPhone3('');
            resetInput(issueNumRef);
            resetInput(issueDateRef);
            resetInput(issueNameRef);
            setBirthNumValue('');
            setInitDepositData(undefined);
            setInitIssuedData(undefined);
            setSingleDate(getToday());
        }, [radioValue]);

        const resetInput = (ref: React.RefObject<HTMLInputElement>) => {
            if (ref.current) {
                ref.current.value = '';
            }
        };

        const [initDepositData, setInitDepositData] =
            useState<SvcGstDtlGetDepositBalanceCertificateResponse>();

        const [initIssuedData, setInitIssuedData] =
            useState<SvcGstDtlGetFindIssuedCertificatesResponse>();

        // 예금잔액 증명서
        const { request: getInitDepositData, isLoading: isLoadingGetInitDepositData } =
            useSvcGstDtlGetDepositBalanceCertificate();

        // 금융거래 부채증명서 발급내역
        const { request: getInitIssuedData, isLoading: isLoadingGetInitIssuedData } =
            useSvcGstDtlGetFindIssuedCertificates();

        // 금융거래 확인서 증명서 보기
        const { request: getInitFinancialData, isLoading: isLoadingGetInitFinancialData } =
            useSvcGstDtlGetFinancialTransactionConfirmation();

        // 부채잔액 증명서 보기
        const { request: getInitLiabilityData, isLoading: isLoadingGetInitLiabilityData } =
            useSvcGstDtlGetLiabilityBalanceCertificate();

        const isValidate = async () => {
            const myName = myNameRef.current?.value ?? ''; // 증명서 소지인
            const phone1 = phone1Ref.current?.value ?? ''; // 휴대폰 앞번호
            const phone2 = phone2Ref.current?.value ?? ''; // 휴대폰 중간번호
            const phone3 = phone3Ref.current?.value ?? ''; // 휴대폰 마지막번호
            const issueNum = issueNumRef.current?.value ?? ''; // 발급번호
            const issueDate = issueDateRef.current?.value ?? ''; // 발급기준일
            const issueName = issueNameRef.current?.value ?? ''; // 예금주
            const birthNum = birthNumRef.current?.value ?? ''; // 생년월일

            if (myName.length === 0) {
                Dialog.alert({
                    content: t('SVCSPD001_21TB.085', '증명서 소지인 이름을 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            myNameRef.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (phone1.length < 3) {
                Dialog.alert({
                    content: t('SVCSPD001_21TB.086', '연락처를 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            phone1Ref.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (phone2.length < 4) {
                Dialog.alert({
                    content: t('SVCSPD001_21TB.086', '연락처를 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            phone2Ref.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (phone3.length < 4) {
                Dialog.alert({
                    content: t('SVCSPD001_21TB.086', '연락처를 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            phone3Ref.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (issueNum.length === 0) {
                Dialog.alert({
                    content: t('SVCSPD001_21TB.087', '발급번호를 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            issueNumRef.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (issueDate.length === 0) {
                Dialog.alert({
                    content: t('SVCSPD001_21TB.094', '발급기준일을 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            issueDateRef.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (issueName.length === 0) {
                Dialog.alert({
                    content: t('SVCSPD001_21TB.088', '예금주를 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            issueNameRef.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (issueName.length !== 0 && accountGroups[0].currency === '000') {
                let fullCharIssueName = formatUtils.toFullChar(issueNameRef.current?.value ?? '');

                if ((fullCharIssueName?.length ?? 0) > 15) {
                    Dialog.alert({
                        content: t('SVCSPD001_21TB.095', '예금주명을 확인해 주세요.'),
                        onClick: async () => {
                            requestAnimationFrame(() => {
                                issueNameRef.current?.focus();
                            });
                        },
                    });

                    return false;
                }
            }

            if (radioValue !== 'accountBalance') {
                // 예금잔액증명서가 아닐때
                if (birthNum.length === 0) {
                    Dialog.alert({
                        content: t(
                            'SVCSPD001_21TB.093',
                            '생년월일/사업자등록번호를 입력해 주세요.',
                        ),
                        onClick: async () => {
                            requestAnimationFrame(() => {
                                birthNumRef.current?.focus();
                            });
                        },
                    });

                    return false;
                }

                if (
                    !(birthNum.length === 6 || birthNum.length === 10 || birthNum.length === 13) &&
                    !(birthNum.length === 0)
                ) {
                    Dialog.alert({
                        content: t(
                            'SVCSPD001_21TB.096',
                            '생년월일/사업자등록번호를 정확히 입력해 주세요.',
                        ),
                        onClick: async () => {
                            requestAnimationFrame(() => {
                                birthNumRef.current?.focus();
                            });
                        },
                    });

                    return false;
                }
            }

            if (radioValue === 'accountBalance') {
                // 예금잔액증명서일때만
                for (var i = 0; i < accountGroups.length; i++) {
                    if (accountGroups[i].accountNumber === '') {
                        Dialog.alert({
                            content: t('SVCSPD001_21TB.089', '계좌번호를 입력해 주세요.'),
                            onClick: async () => {
                                requestAnimationFrame(() => {
                                    acctNumRef.current[i]?.focus();
                                });
                            },
                        });

                        return false;
                    }

                    if (accountGroups[i].accountNumber.length < 11) {
                        Dialog.alert({
                            content: t('SVCSPD001_21TB.090', '계좌번호 11자리를 입력해 주세요.'),
                            onClick: async () => {
                                requestAnimationFrame(() => {
                                    acctNumRef.current[i]?.focus();
                                });
                            },
                        });

                        return false;
                    }

                    if (accountGroups[i].amount === '') {
                        Dialog.alert({
                            content: t('SVCSPD001_21TB.091', '발급금액을 입력해 주세요.'),
                            onClick: async () => {
                                requestAnimationFrame(() => {
                                    issueAmtRef.current[i]?.focus();
                                });
                            },
                        });

                        return false;
                    }

                    if (accountGroups[i].currency === '000') {
                        if (accountGroups[i].amount.indexOf('.') !== -1) {
                            Dialog.alert({
                                content: t(
                                    'SVCSPD001_21TB.097',
                                    "'KRW-대한민국 원'통화코드는 소수점을 제외하고 입력해 주세요.",
                                ),
                                onClick: async () => {
                                    requestAnimationFrame(() => {
                                        issueAmtRef.current[i]?.focus();
                                    });
                                },
                            });

                            return false;
                        }
                    } else {
                        let tempIndex = accountGroups[i].amount.indexOf('.');
                        let tempLength = accountGroups[i].amount.length;

                        if (tempLength - tempIndex != 3) {
                            Dialog.alert({
                                content: t(
                                    'SVCSPD001_21TB.098',
                                    "'KRW-대한민국 원'이외의 통화코드는 소주점 둘째 자리의 금액까지 입력해 주세요.",
                                ),
                                onClick: async () => {
                                    requestAnimationFrame(() => {
                                        issueAmtRef.current[i]?.focus();
                                    });
                                },
                            });

                            return false;
                        }
                    }

                    // 숫자 및 소수점(.) 1개만 가능
                    var regExt = /^([0-9]{1,})([.]?)[0-9]{1,}$/;

                    if (!regExt.test(accountGroups[i].amount)) {
                        Dialog.alert({
                            content: t(
                                'SVCSPD001_21TB.099',
                                '발급금액은 소수점 제외 숫자 또는 소수점 둘째 자리의 숫자만 입력할 수 있어요.',
                            ),
                            onClick: async () => {
                                requestAnimationFrame(() => {
                                    issueAmtRef.current[i]?.focus();
                                });
                            },
                        });

                        return false;
                    }
                }
            }

            return true;
        };

        const searchBtn = useThrottle(async () => {
            if (btnValue.agreeCheck === '' || btnValue.agreeCheck === 'N') {
                Dialog.alert({
                    content: t('SVCSPD001_21TB.083', '개인정보 수집 및 이용에 동의해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            btnRef.current?.focus();
                        });
                    },
                });

                return false;
            }

            const isValid = await isValidate();

            if (!isValid) return false;

            if (radioValue === 'accountBalance') {
                // 예금잔액 증명서
                const depositRec: {
                    yigjjn: string;
                    yigjwkm: string;
                    yigjwno: string;
                    yiwjjan: number;
                    yifxwjjan: number;
                    yigrcurcod: string;
                }[] = [];

                accountGroups.map((item) => {
                    depositRec.push({
                        yigjjn: item.accountNumber.substring(0, 3) ?? '',
                        yigjwkm: item.accountNumber.substring(3, 5) ?? '',
                        yigjwno: item.accountNumber.substring(5, item.accountNumber.length) ?? '',
                        yiwjjan: Number(item.amount),
                        yifxwjjan: Number(item.amount),
                        yigrcurcod: item.currency ?? '',
                    });
                });

                const requestVo = {
                    yicmfna: myNameRef.current?.value as string,
                    yikname: issueNameRef.current?.value as string,
                    yimbrgnno: phone1Ref.current?.value as string,
                    yimbkno: phone2Ref.current?.value as string,
                    yimbtelno: phone3Ref.current?.value as string,
                    yiseqno2: issueNumRef.current?.value as string,
                    yigril: (issueDateRef.current?.value as string).replaceAll('.', ''),
                    depositRec: depositRec,
                };

                const res = await getInitDepositData(requestVo);

                setInitDepositData(res);
            } else {
                let srchgubn = '2';

                if (radioValue === 'transaction') {
                    srchgubn = '2';
                } else {
                    srchgubn = '3';
                }

                const requestVo = {
                    issuebasedt: (issueDateRef.current?.value as string).replaceAll('.', ''),
                    issueno: issueNumRef.current?.value as string,
                    usernm: issueNameRef.current?.value as string,
                    userssn: birthNumRef.current?.value as string,
                    srchgubun: srchgubn,
                };

                const res = await getInitIssuedData(requestVo);

                setInitIssuedData(res);
            }
        }, 1000);

        // 증명서보기
        const issueView = useThrottle(
            async (
                srchgubun: string,
                issuebasedt: string,
                issuedt: string,
                issueno: string,
                usernm: string,
            ) => {
                // 금융거래 확인서
                if (radioValue === 'transaction') {
                    const requestVo = {
                        issueseqno: srchgubun,
                        issuebasedt: issuebasedt,
                        issuedt: issuedt,
                        issueno: issueno,
                        usernm: usernm,
                    };
                    const res = await getInitFinancialData(requestVo);

                    if (res) {
                        const resData = certificatesUtils.fmtEdocFinancialTransact(
                            '1',
                            res as SvcGstDtlGetFinancialTransactionConfirmationResponse,
                        );

                        handleViewClipReport('C', { data: resData });
                    } // END  :: if(res)
                } else {
                    // 부채잔액증명서
                    const requestVo = {
                        issueseqno: srchgubun,
                        issuebasedt: issuebasedt,
                        issuedt: issuedt,
                        issueno: issueno,
                        usernm: usernm,
                    };

                    const res = await getInitLiabilityData(requestVo);

                    if (res) {
                        const langeCd = res.langCd === '1' ? '1' : '2';

                        const resData = certificatesUtils.fmtEdocDebtBalance(
                            langeCd,
                            res as SvcGstDtlGetLiabilityBalanceCertificateResponse,
                        );

                        handleViewLiClipReport('C', { data: resData }, langeCd);
                    } // END  :: if(res)
                }
            },
            1000,
        );

        // 금융거래확인서 클립리포트 호출.
        const handleViewClipReport = useCallback(
            (clipType: 'R' | 'C', crfJsonData: {}) => {
                let [title, pdfFileNm, crfFileNm] = ['금융거래확인서', '', ''];
                const srtDateTime = dateUtils.toTimeString(new Date());

                // 금융거래확인서 원본보기
                if (clipType === 'R') {
                    pdfFileNm = 'CRS_Report_1_' + srtDateTime + '.pdf';
                    crfFileNm = 'CRS_Report_1.crf';
                } else {
                    crfFileNm = 'MA4_SCIS_002.crf';
                    pdfFileNm = 'MA4_SCIS_002_' + srtDateTime + '.pdf';
                }

                const viewParameter: EdocViewParameter = {
                    tranType: EdocTranType.CERTIFICATES, //전자문서 필요.
                    //pdfType: EdocPdfType.NONE,
                    ibReportMode: EdocIbReportMode.VIEW,
                    title: title,
                    pdfFileName: pdfFileNm,
                    crfFileName: crfFileNm,
                    crfJsonData: JSON.stringify(crfJsonData),
                };
                console.log('🌷🌷 viewParameter >>>>>>> 🌷🌷 ', viewParameter);
                edoc.open(viewParameter);
            },
            [edoc],
        );

        // 부채잔액증명서 클립리포트 호출.
        const handleViewLiClipReport = useCallback(
            (clipType: 'R' | 'C', crfJsonData: {}, langCd: '1' | '2') => {
                let [title, pdfFileNm, crfFileNm] = ['부채잔액증명서', '', ''];
                const srtDateTime = dateUtils.toTimeString(new Date());

                if (clipType === 'R') {
                    pdfFileNm = 'CRS_Report_1_' + srtDateTime + '.pdf';
                    crfFileNm = 'CRS_Report_1.crf';
                } else {
                    // 부채잔액증명서 (국문) 원본보기
                    if (langCd === '1') {
                        crfFileNm = 'MA4_SCIS_012.crf';
                        pdfFileNm = 'MA4_SCIS_012_' + srtDateTime + '.pdf';
                    } else {
                        // 부채잔액증명서 (영문) 원본보기
                        crfFileNm = 'MA4_SCIS_014.crf';
                        pdfFileNm = 'MA4_SCIS_014_' + srtDateTime + '.pdf';
                    }
                }
                console.log('🌷🌷 crfJsonData >>>>>>> 🌷🌷 ', crfJsonData);

                const viewParameter: EdocViewParameter = {
                    tranType: EdocTranType.CERTIFICATES, //전자문서 필요.
                    //pdfType: EdocPdfType.NONE,
                    ibReportMode: EdocIbReportMode.VIEW,
                    title: title,
                    pdfFileName: pdfFileNm,
                    crfFileName: crfFileNm,
                    crfJsonData: JSON.stringify(crfJsonData),
                };
                console.log('🌷🌷 viewParameter >>>>>>> 🌷🌷 ', viewParameter);
                edoc.open(viewParameter);
            },
            [edoc],
        );

        return (
            <>
                <FieldWrapper title={t('SVCSPD001_21TB.004', '조회구분')}>
                    <div className="form-item-default-col">
                        <Radio
                            variants="button-radio"
                            id="radioSelect1_1"
                            label={t('SVCSPD001_21TB.005', '예금잔액증명서')}
                            value="accountBalance"
                            className="min-h-[64px]"
                            defaultChecked={radioValue === 'accountBalance'}
                            name="radioSelect1"
                            onChange={() => setRadioValue('accountBalance')}
                        />
                        <Radio
                            variants="button-radio"
                            id="radioSelect1_2"
                            label={t('SVCSPD001_21TB.006', '금융거래확인서')}
                            value="transaction"
                            className="min-h-[64px]"
                            name="radioSelect1"
                            checked={radioValue === 'transaction'}
                            onChange={() => setRadioValue('transaction')}
                        />
                        <Radio
                            variants="button-radio"
                            id="radioSelect1_3"
                            label={t('SVCSPD001_21TB.007', '부채잔액증명서')}
                            value="outstandingBalance"
                            className="min-h-[64px]"
                            name="radioSelect1"
                            checked={radioValue === 'outstandingBalance'}
                            onChange={() => setRadioValue('outstandingBalance')}
                        />
                    </div>
                </FieldWrapper>
                {radioValue === 'accountBalance' && (
                    <div className="bg-basic-background-06 p-8 rounded-[12px] mt-8">
                        <img
                            src={Images.CertificationSample}
                            alt={t('SVCSPD001_21TB.100', '예금/신탁잔액증명서 견본')}
                            width="100%"
                        />
                        <BulletList type="circle" className="mt-5">
                            <BulletListItem>
                                {t(
                                    'SVCSPD001_21TB.008',
                                    '위 증명서 견본을 참고하여 소지한 증명서의 내용을 입력해 주세요.',
                                )}
                            </BulletListItem>
                            <BulletListItem>
                                {t(
                                    'SVCSPD001_21TB.009',
                                    '증명서 조회는 발급일자 포함 30일 이내만 할 수 있어요.',
                                )}
                            </BulletListItem>
                        </BulletList>
                    </div>
                )}
                <div className="flex flex-col gap-8 mt-12">
                    <div>
                        {/* 260416 클래스 수정 */}
                        <div className="page-heading-sm-title mb-4">
                            {t('SVCSPD001_21TB.010', '개인정보의 수집 및 이용 동의')}
                        </div>
                        <div className="border border-basic-04 rounded-[12px] h-[300px] p-5 overflow-y-scroll">
                            {radioValue === 'accountBalance' && (
                                <div className="terms_wrap">
                                    <h2>
                                        [필수] 개인정보 수집·이용 동의서
                                        <br />
                                        [예금잔액증명서 발급 조회용]
                                    </h2>
                                    <div className="content_wrap">
                                        <p className="tit_txt">
                                            * [ ㈜한국스탠다드차타드은행 ] 귀중
                                        </p>
                                        <ul className="s_txt">
                                            <li>
                                                귀 행과의 예금잔액증명서 발급 조회와 관련하여 귀
                                                행이 본인의 개인정보를 수집·이용하고자 하는 경우
                                                「개인정보보호법」 등 관계 법령에 따라 본인의 동의가
                                                필요합니다.
                                            </li>
                                        </ul>

                                        <div className="terms_cont">
                                            <div className="newTermsDepth">
                                                <p className="t_title mt25">수집·이용 목적</p>
                                                <ul className="terms">
                                                    <li>예금잔액증명서 발급 조회</li>
                                                </ul>
                                                <p className="t_title">보유 및 이용기간</p>
                                                <ul className="terms">
                                                    <li>
                                                        <span className="bd_type">
                                                            수집·이용에 관한 동의일로부터 조회서비스
                                                            종료일까지 보유·이용
                                                        </span>
                                                    </li>
                                                    <li className="none bg">
                                                        위 보유 기간에서의 조회서비스 종료일이란
                                                        “신청하신 조회서비스 처리가 종료된 날”을
                                                        말합니다.
                                                        <br />
                                                        <br />
                                                        단, 조회거래 종료일 후에는 금융사고 조사,
                                                        분쟁 해결, 민원 처리, 법령상 의무이행을 위한
                                                        목적으로만 보유·이용됩니다.
                                                    </li>
                                                </ul>
                                                <p className="t_title">거부 권리 및 불이익</p>
                                                <ul className="terms">
                                                    <li className="none">
                                                        귀하는 동의를 거부하실 수 있습니다. 다만, 위
                                                        개인정보 수집·이용에 관한 동의는
                                                        “예금잔액증명서 발급 조회”를 위한 필수적
                                                        사항이므로, 위 사항에 동의하셔야만 조회
                                                        신청이 가능합니다.
                                                    </li>
                                                </ul>
                                                <p className="cr_tit">
                                                    <span>수집·이용 항목</span>
                                                </p>
                                                <p className="t_title mt20">개인(신용)정보</p>
                                                <ul className="terms">
                                                    <li>
                                                        <strong>일반개인정보</strong> : 증명서
                                                        소지인 성명, 연락처(휴대전화번호)
                                                    </li>
                                                </ul>
                                                <div className="agree_ck mtm15">
                                                    <p className="ag_txt">
                                                        위 개인(신용)정보 <span>수집·이용</span>에
                                                        동의하십니까?
                                                    </p>
                                                </div>
                                                <div className="signature">
                                                    <div className="overflow">
                                                        <div className="f_right">
                                                            <span className="sig_name">
                                                                본인 성명 :
                                                            </span>
                                                            <span className="sig">
                                                                (서명 또는 인)
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="overflow">
                                                        <div className="f_right">
                                                            <span className="sig_date">
                                                                <span>년</span>
                                                                <span>월</span>
                                                                <span>일</span>
                                                            </span>
                                                            <span className="sig_name">
                                                                대리인 성명 :
                                                            </span>
                                                            <span className="sig">
                                                                (서명 또는 인)
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {radioValue === 'transaction' && (
                                <div className="terms_wrap">
                                    <h2>
                                        [필수] 개인정보 수집·이용 동의서
                                        <br />
                                        [금융거래확인서 발급 조회]
                                    </h2>
                                    <div className="content_wrap">
                                        <p className="tit_txt">[ ㈜한국스탠다드차타드은행 ] 귀중</p>
                                        <ul className="s_txt">
                                            <li>
                                                귀 행과의 거래확인서 발급 조회거래와 관련하여 귀
                                                행이 본인의 개인정보를 수집·이용하고자 하는 경우에는
                                                「개인정보보호법」등 관계 법령에 따라 본인의 동의가
                                                필요합니다.
                                            </li>
                                        </ul>

                                        <div className="terms_cont">
                                            <div className="newTermsDepth">
                                                <p className="t_title mt25">수집·이용 목적</p>
                                                <ul className="terms">
                                                    <li>금융거래확인서 발급 조회</li>
                                                </ul>
                                                <p className="t_title">보유 및 이용기간</p>
                                                <ul className="terms">
                                                    <li>
                                                        <span className="bd_type">
                                                            수집·이용에 관한 동의일로부터 조회서비스
                                                            종료일까지 보유·이용
                                                        </span>
                                                    </li>
                                                    <li className="none bg">
                                                        위 보유 기간에서의 조회서비스 종료일이란
                                                        “신청하신 금융거래확인서 발급조회서비스
                                                        처리가 종료된 날”을 말합니다.
                                                        <br />
                                                        <br />
                                                        조회거래 종료일 후에는 금융사고 조사, 분쟁
                                                        해결, 민원 처리, 법령상 의무이행을 위한
                                                        목적으로만 보유·이용됩니다.
                                                    </li>
                                                </ul>
                                                <p className="t_title">거부 권리 및 불이익</p>
                                                <ul className="terms">
                                                    <li className="none">
                                                        귀하는 동의를 거부하실 수 있습니다. 다만, 위
                                                        개인정보 수집·이용에 관한 동의는
                                                        “금융거래확인서 발급 조회”를 위한 필수적
                                                        사항이므로, 위 사항에 동의하셔야만
                                                        금융거래확인서 발급조회가 가능합니다.
                                                    </li>
                                                </ul>
                                                <p className="cr_tit">
                                                    <span>수집·이용 항목</span>
                                                </p>
                                                <p className="t_title mt20">개인(신용)정보</p>
                                                <ul className="terms">
                                                    <li>
                                                        <strong>일반개인정보</strong> : 성명, 연락처
                                                    </li>
                                                </ul>
                                                <div className="agree_ck mtm15">
                                                    <p className="ag_txt">
                                                        위 개인(신용)정보 <span>수집·이용</span>에
                                                        동의하십니까?
                                                    </p>
                                                </div>
                                                <div className="signature">
                                                    <div className="overflow">
                                                        <div className="f_right">
                                                            <span className="sig_name">
                                                                본인 성명 :
                                                            </span>
                                                            <span className="sig">
                                                                (서명 또는 인)
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="overflow">
                                                        <div className="f_right">
                                                            <span className="sig_date">
                                                                <span>년</span>
                                                                <span>월</span>
                                                                <span>일</span>
                                                            </span>
                                                            <span className="sig_name">
                                                                대리인 성명 :
                                                            </span>
                                                            <span className="sig">
                                                                (서명 또는 인)
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {radioValue === 'outstandingBalance' && (
                                <div className="terms_wrap">
                                    <h2>
                                        [필수] 개인정보 수집·이용 동의서
                                        <br />
                                        [부채잔액증명서 발급 조회]
                                    </h2>
                                    <div className="content_wrap">
                                        <p className="tit_txt">[ ㈜한국스탠다드차타드은행 ] 귀중</p>
                                        <ul className="s_txt">
                                            <li>
                                                귀 행과의 부채잔액증명서 발급 조회거래와 관련하여 귀
                                                행이 본인의 개인정보를 수집·이용하고자 하는 경우에는
                                                「개인정보보호법」등 관계 법령에 따라 본인의 동의가
                                                필요합니다.
                                            </li>
                                        </ul>

                                        <div className="terms_cont">
                                            <div className="newTermsDepth">
                                                <p className="t_title mt25">수집·이용 목적</p>
                                                <ul className="terms">
                                                    <li>부채잔액증명서 발급 조회</li>
                                                </ul>
                                                <p className="t_title">보유 및 이용기간</p>
                                                <ul className="terms">
                                                    <li>
                                                        <span className="bd_type">
                                                            수집·이용에 관한 동의일로부터 조회서비스
                                                            종료일까지 보유·이용
                                                        </span>
                                                    </li>
                                                    <li className="none bg">
                                                        위 보유 기간에서의 조회서비스 종료일이란
                                                        “신청하신 부채잔액증명서 발급조회서비스
                                                        처리가 종료된 날”을 말합니다.
                                                        <br />
                                                        <br />
                                                        단, 조회거래 종료일 후에는 금융사고 조사,
                                                        분쟁 해결, 민원 처리, 법령상 의무이행을 위한
                                                        목적으로만 보유·이용됩니다.
                                                    </li>
                                                </ul>
                                                <p className="t_title">거부 권리 및 불이익</p>
                                                <ul className="terms">
                                                    <li className="none">
                                                        귀하는 동의를 거부하실 수 있습니다. 다만, 위
                                                        개인정보 수집·이용에 관한 동의는
                                                        “부채잔액증명서 발급 조회”를 위한 필수적
                                                        사항이므로, 위 사항에 동의하셔야만
                                                        부채잔액증명서 발급조회가 가능합니다.
                                                    </li>
                                                </ul>
                                                <p className="cr_tit">
                                                    <span>수집·이용 항목</span>
                                                </p>
                                                <p className="t_title mt20">개인(신용)정보</p>
                                                <ul className="terms">
                                                    <li>
                                                        <strong>일반개인정보</strong> : 성명, 연락처
                                                    </li>
                                                </ul>
                                                <div className="agree_ck mtm15">
                                                    <p className="ag_txt">
                                                        위 개인(신용)정보 <span>수집·이용</span>에
                                                        동의하십니까?
                                                    </p>
                                                </div>
                                                <div className="signature">
                                                    <div className="overflow">
                                                        <div className="f_right">
                                                            <span className="sig_name">
                                                                본인 성명 :
                                                            </span>
                                                            <span className="sig">
                                                                (서명 또는 인)
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="overflow">
                                                        <div className="f_right">
                                                            <span className="sig_date">
                                                                <span>년</span>
                                                                <span>월</span>
                                                                <span>일</span>
                                                            </span>
                                                            <span className="sig_name">
                                                                대리인 성명 :
                                                            </span>
                                                            <span className="sig">
                                                                (서명 또는 인)
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <FieldWrapper
                        title={
                            radioValue === 'accountBalance'
                                ? t(
                                      'SVCSPD001_21TB.011',
                                      '예금잔액증명서 조회를 위한 개인정보 수집 및 이용에 동의하시나요?',
                                  )
                                : radioValue === 'transaction'
                                  ? t(
                                        'SVCSPD001_21TB.012',
                                        '금융거래확인서 조회를 위한 개인정보 수집 및 이용에 동의하시나요?',
                                    )
                                  : t(
                                        'SVCSPD001_21TB.013',
                                        '부채잔액증명서 조회를 위한 개인정보 수집 및 이용에 동의하시나요?',
                                    )
                        }
                    >
                        <div className="flex gap-2" ref={btnRef} tabIndex={-1}>
                            <Radio
                                variants="button"
                                id="radioSelect2_2"
                                label={t('SVCSPD001_21TB.014', '동의안함')}
                                value="N"
                                className="min-w-[130px]"
                                name="radioSelect2"
                                checked={btnValue.agreeCheck === 'N'}
                                onClick={() =>
                                    setBtnValue((prev) => ({ ...prev, agreeCheck: 'N' }))
                                }
                            />
                            <Radio
                                variants="button"
                                id="radioSelect2_1"
                                label={t('SVCSPD001_21TB.015', '동의함')}
                                value="Y"
                                className="min-w-[130px]"
                                name="radioSelect2"
                                checked={btnValue.agreeCheck === 'Y'}
                                onClick={() =>
                                    setBtnValue((prev) => ({ ...prev, agreeCheck: 'Y' }))
                                }
                            />
                        </div>
                    </FieldWrapper>

                    <FieldWrapper
                        labelFor="myName"
                        title={t('SVCSPD001_21TB.016', '증명서 소지인 이름')}
                    >
                        <FieldRound
                            ref={myNameRef}
                            id="myName"
                            placeholder={t('SVCSPD001_21TB.017', '실명(한글) 이름')}
                            title={t('SVCSPD001_21TB.017', '실명(한글) 이름')}
                            maxLength={20}
                        />
                    </FieldWrapper>

                    <FieldWrapper title={t('SVCSPD001_21TB.018', '휴대폰번호')}>
                        <PhoneField
                            selectOption={[
                                {
                                    value: '010',
                                    label: '010',
                                },
                                {
                                    value: '011',
                                    label: '011',
                                },
                                {
                                    value: '013',
                                    label: '013',
                                },
                                {
                                    value: '016',
                                    label: '016',
                                },
                                {
                                    value: '017',
                                    label: '017',
                                },
                                {
                                    value: '018',
                                    label: '018',
                                },
                                {
                                    value: '019',
                                    label: '019',
                                },
                            ]}
                            prefixSelect={{
                                value: phone1,
                                ref: phone1Ref,

                                onChange: (e) => {
                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
                                    setPhone1(onlyNumbers);
                                },
                            }}
                            middleInput={{
                                value: phone2,
                                ref: phone2Ref,
                                onChange: (e) => {
                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
                                    setPhone2(onlyNumbers);
                                },
                            }}
                            lastInput={{
                                value: phone3,
                                ref: phone3Ref,
                                onChange: (e) => {
                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
                                    setPhone3(onlyNumbers);
                                },
                            }}
                        />
                    </FieldWrapper>

                    <FieldWrapper
                        labelFor="issueNum"
                        title={
                            radioValue === 'accountBalance'
                                ? t('SVCSPD001_21TB.019', '① 발급번호')
                                : t('SVCSPD001_21TB.021', '발급번호')
                        }
                    >
                        <FieldRound
                            ref={issueNumRef}
                            id="issueNum"
                            placeholder={
                                radioValue === 'accountBalance'
                                    ? t('SVCSPD001_21TB.020', '- 없이 숫자 17자리')
                                    : t('SVCSPD001_21TB.022', '- 없이 숫자, 영문 16자리')
                            }
                            title={
                                radioValue === 'accountBalance'
                                    ? t('SVCSPD001_21TB.020', '- 없이 숫자 17자리')
                                    : t('SVCSPD001_21TB.022', '- 없이 숫자, 영문 16자리')
                            }
                            inputMode={radioValue === 'accountBalance' ? 'numeric' : 'text'}
                            maxLength={radioValue === 'accountBalance' ? 17 : 16}
                            onKeyUp={(e) => {
                                let value = e.currentTarget.value;
                                let returnValue = '';

                                for (var i = 0; i < value.length; i++) {
                                    if (radioValue === 'accountBalance') {
                                        if (value.charAt(i) >= '0' && value.charAt(i) <= '9') {
                                            returnValue += value.charAt(i);
                                        }
                                    } else {
                                        if (
                                            (value.charAt(i) >= '0' && value.charAt(i) <= '9') ||
                                            (value.charAt(i) >= 'A' && value.charAt(i) <= 'Z') ||
                                            (value.charAt(i) >= 'a' && value.charAt(i) <= 'z')
                                        ) {
                                            returnValue += value.charAt(i);
                                        }
                                    }
                                }

                                e.currentTarget.value = returnValue.toUpperCase();
                            }}
                        />
                    </FieldWrapper>

                    <FieldWrapper
                        labelFor="issueDate"
                        title={
                            radioValue === 'accountBalance'
                                ? t('SVCSPD001_21TB.023', '② 발급기준일')
                                : t('SVCSPD001_21TB.024', '발급기준일')
                        }
                    >
                        <DatePickerSingle
                            ref={issueDateRef}
                            id="issueDate"
                            value={singleDate}
                            onChange={setSingleDate}
                            startYear={new Date(currYear - 10, 0)}
                            endYear={new Date(currYear, 11)}
                            disabledDate={(date) => {
                                return date > new Date(limitDate);
                            }}
                            mode="picker"
                        />
                    </FieldWrapper>

                    <FieldWrapper
                        labelFor="issueName"
                        title={
                            radioValue === 'accountBalance'
                                ? t('SVCSPD001_21TB.025', '③ 예금주')
                                : t('SVCSPD001_21TB.026', '예금주')
                        }
                    >
                        <FieldRound
                            ref={issueNameRef}
                            id="issueName"
                            placeholder={
                                radioValue === 'accountBalance'
                                    ? t('SVCSPD001_21TB.028', '영문이름은 대문자 입력')
                                    : t('SVCSPD001_21TB.027', '증명서 상 예금주 입력')
                            }
                            title={
                                radioValue === 'accountBalance'
                                    ? t('SVCSPD001_21TB.028', '영문이름은 대문자 입력')
                                    : t('SVCSPD001_21TB.027', '증명서 상 예금주 입력')
                            }
                            maxLength={30}
                        />
                    </FieldWrapper>

                    {radioValue !== 'accountBalance' && (
                        <FieldWrapper
                            labelFor="birthNum"
                            title={t('SVCSPD001_21TB.029', '생년월일/사업자등록번호')}
                        >
                            <FieldRound
                                ref={birthNumRef}
                                id="birthNum"
                                maxLength={13}
                                placeholder={t(
                                    'SVCSPD001_21TB.030',
                                    '생년월일 6자리/사업자등록번호 10자리',
                                )}
                                title={t(
                                    'SVCSPD001_21TB.030',
                                    '생년월일 6자리/사업자등록번호 10자리',
                                )}
                                value={birthNumValue}
                                onChange={(e) => {
                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');

                                    setBirthNumValue(onlyNumbers);
                                }}
                            />
                        </FieldWrapper>
                    )}

                    {radioValue === 'accountBalance' && (
                        <div className="flex flex-col mt-3">
                            <div>
                                <div className="flex justify-between items-center mb-5">
                                    <div className="page-heading-sm-title">
                                        {t('SVCSPD001_21TB.032', '계좌추가')}
                                    </div>
                                    {accountGroups.length < MaxAccountCount && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={handleAddGroup}
                                        >
                                            {t('SVCSPD001_21TB.101', '추가')}
                                        </Button>
                                    )}
                                </div>
                                {accountGroups.map((group, index) => (
                                    <FieldWrapper
                                        className="[&+&]:mt-8"
                                        key={`account-group-${index}`}
                                        labelFor="native-select"
                                        title={t('SVCSPD001_21TB.102', '계좌') + `${index + 1}`}
                                        rightButton={
                                            index > 0 ? (
                                                <button
                                                    type="button"
                                                    // aria-label={t('SVCSPD001_21TB.103', '삭제')}
                                                    aria-label={
                                                        t('SVCSPD001_21TB.102', '계좌') +
                                                        `${index + 1}` +
                                                        t('SVCSPD001_21TB.103', '삭제')
                                                    }
                                                    onClick={() => {
                                                        setAccountGroups((prev) =>
                                                            prev.filter(
                                                                (item) => item.id !== group.id,
                                                            ),
                                                        );
                                                    }}
                                                >
                                                    <LineIcon
                                                        icoColor="bg-icon-basic-01"
                                                        icoName="ico-trash"
                                                    />
                                                </button>
                                            ) : undefined
                                        }
                                    >
                                        <NativeSelect
                                            id="native-select"
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setAccountGroups((prev) =>
                                                    prev.map((item) =>
                                                        item.id == group.id
                                                            ? { ...item, currency: value }
                                                            : item,
                                                    ),
                                                );
                                            }}
                                        >
                                            {selectCodes('CURRENCY').map((value) => (
                                                <>
                                                    <NativeSelectOption value={value.value}>
                                                        {value.label}
                                                    </NativeSelectOption>
                                                </>
                                            ))}
                                        </NativeSelect>
                                        <FieldRound
                                            ref={(el) => (acctNumRef.current[index] = el)}
                                            placeholder={t(
                                                'SVCSPD001_21TB.056',
                                                '-없이 숫자만 입력(④ 계좌번호)',
                                            )}
                                            title={t(
                                                'SVCSPD001_21TB.056',
                                                '-없이 숫자만 입력(④ 계좌번호)',
                                            )}
                                            value={group.accountNumber.replace(/[^0-9]/g, '')}
                                            maxLength={12}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^0-9]/g, '');

                                                setAccountGroups((prev) =>
                                                    prev.map((item) =>
                                                        item.id == group.id
                                                            ? { ...item, accountNumber: value }
                                                            : item,
                                                    ),
                                                );
                                            }}
                                        />
                                        <FieldRound
                                            ref={(el) => (issueAmtRef.current[index] = el)}
                                            placeholder={t(
                                                'SVCSPD001_21TB.057',
                                                '-없이 숫자만 입력(⑤ 발급금액)',
                                            )}
                                            title={t(
                                                'SVCSPD001_21TB.057',
                                                '-없이 숫자만 입력(⑤ 발급금액)',
                                            )}
                                            maxLength={12}
                                            value={group.amount.replace(/[^0-9.]/g, '')}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(
                                                    /[^0-9.]/g,
                                                    '',
                                                );

                                                setAccountGroups((prev) =>
                                                    prev.map((item) =>
                                                        item.id == group.id
                                                            ? { ...item, amount: value }
                                                            : item,
                                                    ),
                                                );
                                            }}
                                        />
                                    </FieldWrapper>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-center">
                        <Button
                            size="md"
                            variant="outline"
                            onClick={searchBtn}
                            disabled={isLoadingGetInitDepositData || isLoadingGetInitIssuedData}
                        >
                            {t('SVCSPD001_21TB.031', '조회')}
                            <LineIcon
                                icoColor="bg-icon-primary-04"
                                icoName="ico-search"
                                type="xs"
                            />
                        </Button>
                    </div>
                </div>

                <div className="mt-12">
                    {radioValue === 'accountBalance' &&
                        initDepositData?.yooutgb &&
                        (initDepositData?.yooutgb === 'Y' ? (
                            <GradientInfoBox>
                                <TitleList
                                    type="inverse"
                                    title={t('SVCSPD001_21TB.064', '조회결과')}
                                    desc={t(
                                        'SVCSPD001_21TB.065',
                                        '예금잔액증명서 발급 내역과 일치해요.',
                                    )}
                                />
                            </GradientInfoBox>
                        ) : (
                            ''
                        ))}
                    {radioValue !== 'accountBalance' &&
                        initIssuedData?.listtotcnt !== 0 &&
                        initIssuedData?.listsel.map((data) => {
                            let srchgubunNm = '';
                            let issueno = data.issueno;

                            if (data.srchgubun === '2') {
                                srchgubunNm = t('SVCSPD001_21TB.069', '금융거래 확인서');
                            } else if (data.srchgubun === '3') {
                                srchgubunNm = t('SVCSPD001_21TB.070', '부채잔액 증명서');
                            }

                            return (
                                <>
                                    <GradientInfoBox>
                                        <InfoListGroup type="minimum">
                                            <InfoListItem
                                                title={t('SVCSPD001_21TB.068', '조회구분')}
                                                desc={srchgubunNm}
                                            />
                                            <InfoListItem
                                                title={t('SVCSPD001_21TB.071', '예금주')}
                                                desc={data.usernm}
                                            />
                                            <InfoListItem
                                                title={t('SVCSPD001_21TB.072', '발급기준일')}
                                                desc={formatUtils.toDate(data.issuebasedt, '.')}
                                            />
                                            <InfoListItem
                                                title={t('SVCSPD001_21TB.073', '발급번호')}
                                                desc={
                                                    issueno.substring(0, 8) +
                                                    '-' +
                                                    issueno.substring(8, 11) +
                                                    '-' +
                                                    issueno.substring(11, 16)
                                                }
                                            />
                                        </InfoListGroup>
                                    </GradientInfoBox>
                                    <div className="flex items-center justify-center">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="mt-6"
                                            onClick={() =>
                                                issueView(
                                                    data.srchgubun,
                                                    data.issuebasedt,
                                                    data.issuedt,
                                                    data.issueno,
                                                    data.usernm,
                                                )
                                            }
                                            disabled={
                                                isLoadingGetInitFinancialData ||
                                                isLoadingGetInitLiabilityData
                                            }
                                        >
                                            {t('SVCSPD001_21TB.074', '증명서 보기')}
                                        </Button>
                                        {/* 전자문서 뷰어(view) 사용시 전자문서 뷰어 컴포넌트 추가 onComplete={handleEdocViewComplete} */}
                                        {edoc.isOpen && <EdocViewComponent {...edoc} />}
                                    </div>
                                </>
                            );
                        })}

                    {radioValue === 'accountBalance' &&
                        (initDepositData?.yooutgb === 'N' ? (
                            <Empty
                                text={t('SVCSPD001_21TB.066', '예금잔액증명서 발급 내역이 없어요.')}
                                description={t(
                                    'SVCSPD001_21TB.067',
                                    '해당 영업점으로 문의해 주세요.',
                                )}
                                className="mt-8"
                            />
                        ) : (
                            ''
                        ))}

                    {radioValue !== 'accountBalance' &&
                        (initIssuedData?.listtotcnt === 0 ? (
                            <Empty
                                text={
                                    radioValue === 'transaction'
                                        ? t(
                                              'SVCSPD001_21TB.075',
                                              '금융거래확인서 발급 내역이 없어요.',
                                          )
                                        : t(
                                              'SVCSPD001_21TB.076',
                                              '부채잔액증명서 발급 내역이 없어요.',
                                          )
                                }
                                description={t(
                                    'SVCSPD001_21TB.077',
                                    '해당 영업점으로 문의해 주세요.',
                                )}
                                className="mt-8"
                            />
                        ) : (
                            ''
                        ))}
                </div>
                <AccordionUnder openTitle={t('SVCSPD001_21TB.078', '알아두세요')}>
                    {radioValue === 'accountBalance' ? (
                        <BulletList type="circle">
                            <BulletListItem>
                                {t(
                                    'SVCSPD001_21TB.079',
                                    '예금잔액증명서는 동일 잔액증명서에서 최대 7개 계좌까지 조회할 수 있어요.',
                                )}
                            </BulletListItem>
                            <BulletListItem>
                                {t(
                                    'SVCSPD001_21TB.080',
                                    '증명서 조회는 발급일 포함 30일 이내만 가능해요.',
                                )}
                            </BulletListItem>
                        </BulletList>
                    ) : (
                        <BulletList type="circle">
                            <BulletListItem>
                                {t(
                                    'SVCSPD001_21TB.081',
                                    '발급번호 및 생년월일/사업자등록번호는 -없이 입력해 주세요.',
                                )}
                            </BulletListItem>
                            <BulletListItem>
                                {t(
                                    'SVCSPD001_21TB.082',
                                    '증명서 조회는 발급일 포함 최대 3개월 이내만 가능해요.',
                                )}
                            </BulletListItem>
                        </BulletList>
                    )}
                </AccordionUnder>
            </>
        );
    },
);
